import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiResult } from '../application-api/application-api.types';
import {
  CompareAndFraudApplicant,
  ContactInformation,
  Identification,
  InstaTouchSession,
  InstaTouchUserInformation,
  Name,
  InstatouchAutofillApplicants,
  OAOCompareAndFraudApplicant,
  InstaTouchAddress,
  OAOInstatouchVerification,
  GetPersonalInformationParams
} from '../instaTouch/insta-touch.types';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { OAOApplicantType, OAOSourceApplicantType } from '@workflow/ecp-shared/enum/enum';
import { CrudApiBackend } from '@workflow/class/crud-api-backend';
import { StorageService } from '@workflow/service/storage/storage.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { LoanType } from '@shared/enum';
import { StatefulService } from '@app/service/stateful.service';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';

/**
 * Request methods for application related calls
 */
@Injectable({
  providedIn: 'root'
})
export class InstaTouchService {
  /**
   * HostName
   */
  private readonly hostName = 'consumerPortalApi';

  /**
   * Api Root URL.
   */
  protected apiRoot: string;

  /**
   * API backend for mortgage
   */
  // eslint-disable-next-line prettier/prettier
  protected get crudApiService() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return this.storageService.backends.api as CrudApiBackend;
  }

  constructor(
    protected readonly httpClient: HttpClient,
    protected readonly apiService: ApiService,
    protected readonly storageService: StorageService,
    public readonly userService: UserService,
    private readonly statefulService: StatefulService
  ) {}

  protected httpOptions(): { headers: HttpHeaders } {
    let httpHeaders = this.crudApiService?.headers && this.crudApiService.headers();
    if (!(httpHeaders instanceof HttpHeaders)) {
      httpHeaders = new HttpHeaders();
    }
    const authToken = this.getAuthTokenBasedOnFlowType;
    // Set flow based token for dxSessionId and Authorization headers
    httpHeaders = httpHeaders.set(RequestHeaderConst.dxSessionId, authToken).set(RequestHeaderConst.authorization, authToken);
    return { headers: httpHeaders };
  }

  /**
   * Method to get the correct Auth Token based on flow type: OLA or OAO
   */
  protected get getAuthTokenBasedOnFlowType(): string {
    const olaToken = this.statefulService.wrap('ss:auth-ola').valueString;
    const oaoToken = this.statefulService.wrap('ss:authorization').valueString;
    return this.userService?.flowType?.toLowerCase() === LoanType.OLA.toLowerCase() ? olaToken : oaoToken;
  }

  public generatePasscode(mobileNumber: string): Observable<ApiResult<InstaTouchSession>> {
    this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
    const url = this.apiRoot + 'member/oao/integration/GeneratePasscode';
    return this.httpClient.post<ApiResult<InstaTouchSession>>(url, mobileNumber, this.httpOptions());
  }

  public validatePasscode(sessionId: string, passcode: string, requestId: string): Observable<ApiResult<boolean>> {
    const requestBody = {
      payload: { passcode },
      sessionId,
      requestId
    };
    this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
    const url = this.apiRoot + 'member/oao/integration/ValidatePasscode';
    return this.httpClient.post<ApiResult<boolean>>(url, requestBody, this.httpOptions());
  }

  public getPersonalInformation({
    sessionId,
    zipCode,
    last4ssn,
    requestId,
    isPrimaryBorrower = false,
    mobileNumber
  }: GetPersonalInformationParams): Observable<ApiResult<InstaTouchUserInformation>> {
    const requestBody = {
      payload: { zipCode, ssn: last4ssn, isPrimaryBorrower, mobileNumber },
      sessionId,
      requestId
    };
    this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
    const url = this.apiRoot + 'member/oao/integration/PersonalInformation';
    return this.httpClient.post<ApiResult<InstaTouchUserInformation>>(url, requestBody, this.httpOptions());
  }

  public getCompareandFraudResponse(applicant: OAOCompareAndFraudApplicant[]): Observable<ApiResult<OAOInstatouchVerification[]>> {
    this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
    const url = this.apiRoot + 'member/oao/integration/comparefraudverification';
    return this.httpClient.post<ApiResult<OAOInstatouchVerification[]>>(url, applicant, this.httpOptions());
  }

  public runOLACompareAndFraudVerification(): Observable<ApiResult<OAOInstatouchVerification[]>> {
    this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
    const url = this.apiRoot + 'consumer/ola/dynamic/RunCompareFraudVerification';
    return this.httpClient.get<ApiResult<OAOInstatouchVerification[]>>(url, this.httpOptions());
  }

  public getOAOComapreAndFraudApplicantFromBundle(apiPrimaryRoot: string): CompareAndFraudApplicant {
    const names: Name = {
      firstName: this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueString,
      lastName: this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueString
    };
    const zipCode = this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/zipCode`, 0).valueString;
    const fiveDigitZipCode = zipCode && zipCode.length > 5 ? zipCode.substring(0, 5) : zipCode;

    const currentAddress: InstaTouchAddress = {
      city: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/city`, 0).valueString,
      state: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/state`, 0).valueString,
      streetAddress: this.statefulService.wrap(`${apiPrimaryRoot}/primaryAddress/$/addressLine1`, 0).valueString,
      type: 'Current',
      zipCode: fiveDigitZipCode
    };

    const addresses: InstaTouchAddress[] = [currentAddress];
    const homePhone = this.statefulService.wrap(`${apiPrimaryRoot}/homePhone`).valueString;
    const mobileNumber = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).valueString;
    const phoneNumber = homePhone ? homePhone : mobileNumber;
    const formattedPhoneNumber = phoneNumber ? phoneNumber.replace(/\D/g, '') : phoneNumber;
    const contacts: ContactInformation = {
      homePhone: formattedPhoneNumber
    };

    const formattedSsn = this.statefulService.wrap(`${apiPrimaryRoot}/ssn`).valueString;
    const identifications: Identification = {
      dob: this.statefulService.wrap(`${apiPrimaryRoot}/dateOfBirth`).valueString,
      ssn: formattedSsn ? formattedSsn.replace(/-/g, '') : formattedSsn
    };

    const applicant: CompareAndFraudApplicant = {
      addresses,
      contact: contacts,
      identification: identifications,
      name: names
    };
    return applicant;
  }

  /**
   * Retrieves a list of `OAOCompareAndFraudApplicant` objects based on the current membership type.
   *
   * This method determines the membership type from the stateful service and delegates
   * to the appropriate handler for 'personal' or 'business' membership types.
   * If the membership type is not recognized or not present, an empty array is returned.
   *
   * @param path - The path used for processing personal membership applicants.
   * @returns An array of `OAOCompareAndFraudApplicant` objects relevant to the membership type,
   *          or an empty array if the membership type is not set or unrecognized.
   */
  public getOAOComapreAndFraudApplicant(path: string, pageIteration: number): OAOCompareAndFraudApplicant[] {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;

    if (!membershipTypeName) {
      return [];
    }

    const membershipType = membershipTypeName.toLowerCase();

    if (membershipType === 'personal') {
      return this.handlePersonalMembership(path, membershipTypeName);
    }

    if (membershipType === 'business') {
      return this.handleBusinessMembership(path, pageIteration);
    }

    return [];
  }

  private handlePersonalMembership(path: string, membershipTypeName: string): OAOCompareAndFraudApplicant[] {
    const primaryApplicantOption = this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillOption`).valueStringOnly;

    // Early return if not mobile number
    if (!primaryApplicantOption || primaryApplicantOption.toLowerCase() !== 'mobilenumber') {
      return [];
    }

    const applicantId = this.statefulService.wrap(`api:oaoMemberInformation/${path}/applicantId`).valueNumberOnly;
    const applicantTypeID = this.getApplicantTypeId(path);
    const applicantFraudResult = this.getOAOFraudResults();
    // Early return if applicant already exists in fraud results
    // This means we already ran the fraud check for this applicant
    if (
      !applicantTypeID ||
      applicantFraudResult.some((result) => result.applicantTypeId === applicantTypeID && result.applicantId === applicantId)
    ) {
      return [];
    }

    const objApplicant = this.getOAOComapreAndFraudApplicantFromBundle(`api:oaoMemberInformation/${path}`);

    return [
      {
        applicantId,
        applicantTypeID,
        sourceApplicantType: OAOSourceApplicantType.Personal,
        userIdentity: objApplicant,
        applicantEmail: this.statefulService.wrap(`api:oaoMemberInformation/${path}/email`).valueString,
        membershipTypeName
      } as OAOCompareAndFraudApplicant
    ];
  }

  private handleBusinessMembership(path: string, pageIteration: number): OAOCompareAndFraudApplicant[] {
    const applicantList: OAOCompareAndFraudApplicant[] = [];
    const sourceType =
      path === 'responsibleParties'
        ? OAOSourceApplicantType.ResponsibleParty
        : path === 'additionalBeneficialOwner'
        ? OAOSourceApplicantType.BeneficialOwner
        : OAOSourceApplicantType.AuthorizedSigners;

    const propertyCountName =
      sourceType === OAOSourceApplicantType.ResponsibleParty
        ? 'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
        : sourceType === OAOSourceApplicantType.BeneficialOwner
        ? 'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
        : 'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount';

    const applicantsCount = this.statefulService.wrap(propertyCountName).valueStringOnly;

    if (!applicantsCount || Number.parseInt(applicantsCount) <= 0) {
      return;
    }

    const applicant = this.createBusinessApplicant({
      basePath: `api:oaoMemberInformation/businessInformation/${path}`,
      index: pageIteration,
      sourceType,
      includeApplicantId: sourceType === OAOSourceApplicantType.ResponsibleParty
    });

    if (applicant) {
      applicantList.push(applicant);
    }

    return applicantList;
  }

  private createBusinessApplicant({
    basePath,
    index,
    sourceType,
    includeApplicantId = false
  }: {
    basePath: string;
    index: number;
    sourceType: OAOSourceApplicantType;
    includeApplicantId?: boolean;
  }): OAOCompareAndFraudApplicant | null {
    const autoFillOption = this.statefulService.wrap(`${basePath}/$/autoFillOption`, index).valueStringOnly;
    if (!autoFillOption || autoFillOption.toLowerCase() !== 'mobilenumber') {
      return null;
    }

    const applicantId = includeApplicantId ? this.statefulService.wrap(`${basePath}/${index}/applicantId`).valueNumberOnly : null;
    // Check if applicant already exists in fraud results
    // This means we already ran the fraud check for this applicant
    const applicantFraudResult = this.getOAOFraudResults();
    const applicantTypeId = includeApplicantId ? sourceType : index;
    if (applicantFraudResult.some((result) => result.applicantTypeId === applicantTypeId && result.applicantId === applicantId)) {
      return null;
    }

    const objApplicant = this.getOAOComapreAndFraudApplicantFromBundle(`${basePath}/${index}`);

    return {
      applicantId,
      applicantTypeID: applicantTypeId,
      sourceApplicantType: sourceType,
      userIdentity: objApplicant,
      applicantEmail: this.statefulService.wrap(`${basePath}/${index}/email`).valueString
    } as OAOCompareAndFraudApplicant;
  }

  private getApplicantTypeId(path: string): number {
    const pathMapping = {
      primaryApplicant: OAOApplicantType.Primary,
      coApplicant: OAOApplicantType.CoApplicant,
      additionalCoApplicant: OAOApplicantType.Borrower3
    };

    return pathMapping[path] || OAOApplicantType.Borrower3;
  }

  private getOAOFraudResults(): { applicantId: number; applicantTypeId: number }[] {
    const applicantFraudResult: { applicantId: number; applicantTypeId: number }[] = [];
    const fraudResults = this.statefulService.arraySize('api:oaoMemberInformation/fraudAndCompareVerification');

    for (let i = 0; i < fraudResults; i++) {
      const applicantTypeId = this.statefulService.wrap(
        `api:oaoMemberInformation/fraudAndCompareVerification/${i}/applicantTypeID`
      ).valueNumberOnly;
      const applicantId = this.statefulService.wrap(
        `api:oaoMemberInformation/fraudAndCompareVerification/${i}/applicantId`
      ).valueNumberOnly;

      applicantFraudResult.push({
        applicantId,
        applicantTypeId
      });
    }

    return applicantFraudResult;
  }
}
